import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { saveAs } from 'file-saver';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { addNewData, getAllListData } from 'services/CommonServices';
import { MASTER_ENDPOINT } from 'utils/Constant';

import { PdfViewer } from '../viewer';
import { FileUploadModal } from './FileUploadModal';

interface TabContentProps {
  activeTab: number;
  candidateData?: any;
  candidateId?: string;
  jobData?: any;
}

// Separate Notes Component to properly use React hooks
const NotesComponent: React.FC<{ candidateId?: string }> = ({
  candidateId,
}) => {
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isHighlight, setIsHighlight] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getNotes = useCallback(() => {
    if (!candidateId) return;
    setLoading(true);
    getAllListData(
      MASTER_ENDPOINT.Candidates +
        '/notes?id=&userId=&candidateId=' +
        candidateId,
    )
      .then((resp) => {
        setNotes(resp?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [candidateId]);

  useEffect(() => {
    getNotes();
  }, [getNotes]);

  const handleSubmitNote = () => {
    if (!noteText.trim() || !candidateId) return;

    setSubmitting(true);
    const noteData = {
      candidateId: candidateId,
      isHighlight: isHighlight,
      notes: noteText.trim(),
      type: 'resumeValidation',
    };

    addNewData(noteData, MASTER_ENDPOINT.Candidates + '/notes', {}, true)
      .then(() => {
        setNoteText('');
        setIsHighlight(false);
        getNotes();
        setSubmitting(false);
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
      });
  };

  const getTimeAgo = (date: string) => {
    const now = moment();
    const noteDate = moment(date);
    const hours = now.diff(noteDate, 'hours');
    if (hours < 1) return 'Just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    const days = now.diff(noteDate, 'days');
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const getInitials = (firstName: string, lastName: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last;
  };

  if (!candidateId) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '200px',
          justifyContent: 'center',
          padding: '24px',
        }}>
        <Typography
          sx={{
            color: '#6b7280',
            fontFamily: 'Red Hat Display Regular',
            fontSize: '14px',
          }}>
          No candidate selected
        </Typography>
      </Box>
    );
  }

  // Separate notes into highlights and general
  const highlights = notes.filter((note) => note.isHighlight === true);
  const generalNotes = notes.filter(
    (note) => !note.isHighlight || note.isHighlight === false,
  );

  // Render a single note card
  const renderNoteCard = (note: any, isHighlightNote: boolean) => {
    const user = note.user || {};
    const firstName = String(user.firstName || '');
    const lastName = String(user.lastName || '');
    const userName = `${firstName} ${lastName}`.trim() || 'Unknown User';
    const avatarBgColor = isHighlightNote ? '#be0000' : '#616161';

    return (
      <Box
        key={note.id || Math.random()}
        sx={{
          backgroundColor: isHighlightNote ? '#fff3f3' : '#fafafa',
          border: '1px solid transparent',
          borderRadius: '4px',
          marginBottom: '10px',
        }}>
        {/* Card Header */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '12px',
            padding: '12px',
          }}>
          {/* Avatar */}
          <Avatar
            sx={{
              backgroundColor: avatarBgColor,
              fontFamily: 'Red Hat Display Regular',
              fontSize: '12px',
              height: '32px',
              width: '32px',
            }}>
            {getInitials(firstName, lastName)}
          </Avatar>

          {/* Name and Time */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#242424',
                fontFamily: 'Red Hat Display SemiBold',
                fontSize: '13px',
                lineHeight: '20px',
                marginBottom: '2px',
              }}>
              {userName}
            </Typography>
            <Typography
              sx={{
                color: '#616161',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                lineHeight: '16px',
              }}>
              {getTimeAgo(note.createdAt)}
            </Typography>
          </Box>

          {/* Menu Button */}
          <IconButton
            sx={{
              height: '32px',
              padding: '6px',
              width: '32px',
            }}>
            <MoreVertIcon sx={{ color: '#616161', fontSize: '20px' }} />
          </IconButton>
        </Box>

        {/* Card Body */}
        <Box sx={{ padding: '0 12px 12px 12px' }}>
          <Typography
            sx={{
              color: '#424242',
              fontFamily: 'Red Hat Display Regular',
              fontSize: '12px',
              lineHeight: '16px',
              whiteSpace: 'pre-wrap',
            }}>
            {note.notes}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: 'calc(100vh - 230px)',
        overflow: 'hidden',
      }}>
      {/* Notes List Container */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          gap: '16px',
          minHeight: 0,
          overflow: 'hidden',
        }}>
        {/* Left Column - Notes List */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: '10px',
            overflow: 'auto',
            padding: '6px 4px 6px 16px',
          }}>
          {/* Highlights Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography
              sx={{
                color: '#000000',
                fontFamily: 'Red Hat Display Medium',
                fontSize: '13px',
                fontWeight: 500,
              }}>
              Highlights
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {loading ? (
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  Loading...
                </Typography>
              ) : highlights.length > 0 ? (
                highlights.map((note) => renderNoteCard(note, true))
              ) : (
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  No highlights yet
                </Typography>
              )}
            </Box>
          </Box>

          {/* Divider */}
          {highlights.length > 0 && (
            <Box
              sx={{
                borderBottom: '1px solid #e0e0e0',
                height: '12px',
                width: '100%',
              }}
            />
          )}

          {/* General Notes Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography
              sx={{
                color: '#000000',
                fontFamily: 'Red Hat Display Medium',
                fontSize: '13px',
                fontWeight: 500,
              }}>
              General Notes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {loading ? (
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  Loading...
                </Typography>
              ) : generalNotes.length > 0 ? (
                generalNotes.map((note) => renderNoteCard(note, false))
              ) : (
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  No notes yet
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Input Section */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px 0px 16px 0',
        }}>
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '56px',
            overflow: 'hidden',
            padding: '10px',
          }}>
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flex: 1,
              gap: '10px',
            }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Add your feedback notes or comments here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey && noteText.trim()) {
                  handleSubmitNote();
                }
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  '&::placeholder': {
                    color: '#707070',
                  },
                  color: '#707070',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '13px',
                },
              }}
              sx={{ flex: 1 }}
            />
            <Box
              component="button"
              onClick={handleSubmitNote}
              disabled={!noteText.trim() || submitting}
              sx={{
                '&:hover': {
                  backgroundColor:
                    noteText.trim() && !submitting ? '#1a1a1a' : '#2d2d2d',
                },
                alignItems: 'center',
                backgroundColor: '#2d2d2d',
                border: '1px solid #dddddd',
                borderRadius: '6px',
                color: '#ffffff',
                cursor:
                  noteText.trim() && !submitting ? 'pointer' : 'not-allowed',
                display: 'flex',
                flexShrink: 0,
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                gap: '6px',
                height: '32px',
                lineHeight: '16px',
                opacity: noteText.trim() && !submitting ? 1 : 0.6,
                padding: '4px 12px',
              }}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  height: '10px',
                  justifyContent: 'center',
                  width: '12.5px',
                }}>
                <Typography sx={{ color: '#ffffff', fontSize: '10px' }}>
                  →
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: '#ffffff',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '12px',
                }}>
                Submit
              </Typography>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isHighlight}
                onChange={(e) => setIsHighlight(e.target.checked)}
                size="small"
                sx={{
                  '&.Mui-checked': {
                    color: '#be0000',
                  },
                  color: '#be0000',
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: '#424242',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '12px',
                }}>
                Mark as Highlight
              </Typography>
            }
            sx={{ marginLeft: 0, marginTop: '8px' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// Separate Attachments Component to properly use React hooks
const AttachmentsComponent: React.FC<{
  candidateId?: string;
  candidateData?: any;
  jobData?: any;
}> = ({ candidateId, candidateData, jobData }) => {
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<Set<string>>(
    new Set(),
  );

  // Check if candidate is already shortlisted (highlight)
  const isShortlisted = candidateData?.isShortlisted || jobData?.isShortlisted;

  const getAttachments = useCallback(() => {
    if (!candidateId) return;
    setLoading(true);
    getAllListData(
      `${MASTER_ENDPOINT.Candidates}/attachments?candidateId=${candidateId}`,
    )
      .then((resp) => {
        setAttachments(resp?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [candidateId]);

  useEffect(() => {
    getAttachments();
  }, [getAttachments]);

  const handleDownload = useCallback(
    (attachment: any) => {
      if (!attachment?.id || !candidateId) return;

      const url = `${process.env.REACT_APP_APIURL}${
        MASTER_ENDPOINT.Files
      }/${String(attachment.id)}?isDownload=true&userId=${String(candidateId)}`;
      const fileName =
        attachment.fileName ||
        attachment.name ||
        `attachment-${String(attachment.id)}`;

      saveAs(url, fileName);
    },
    [candidateId],
  );

  const handleView = useCallback((attachment: any) => {
    setSelectedFile(attachment);
    setViewModalOpen(true);
  }, []);

  const handleCheckboxChange = useCallback((attachmentId: string) => {
    setSelectedCheckboxes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(attachmentId)) {
        newSet.delete(attachmentId);
      } else {
        newSet.add(attachmentId);
      }
      return newSet;
    });
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedFile(null);
  }, []);

  const getTimeAgo = (date: string) => {
    const now = moment();
    const fileDate = moment(date);
    const hours = now.diff(fileDate, 'hours');
    if (hours < 1) return 'Just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    const days = now.diff(fileDate, 'days');
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const getUserName = (user: any) => {
    if (!user) return 'Unknown User';
    const firstName = String(user.firstName || '');
    const lastName = String(user.lastName || '');
    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  };

  // Render attachment card
  const renderAttachmentCard = (attachment: any) => {
    const showCheckbox = isShortlisted;
    const isChecked = selectedCheckboxes.has(attachment.id);

    return (
      <Box
        key={attachment.id || Math.random()}
        sx={{
          backgroundColor: '#fafafa',
          border: '1px solid transparent',
          borderRadius: '4px',
          marginBottom: '16px',
          padding: '12px',
        }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '12px',
          }}>
          {/* Checkbox - only for highlights already shortlisted */}
          {showCheckbox && (
            <Checkbox
              checked={isChecked}
              onChange={() => handleCheckboxChange(attachment.id)}
              size="small"
              sx={{
                '&.Mui-checked': {
                  color: '#242424',
                },
                color: '#242424',
                padding: '6px',
              }}
            />
          )}

          {/* File Icon */}
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
              height: '32px',
              justifyContent: 'center',
              width: '32px',
            }}>
            <InsertDriveFileIcon
              sx={{
                color: '#616161',
                fontSize: '32px',
              }}
            />
          </Box>

          {/* File Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: '#242424',
                fontFamily: 'Red Hat Display SemiBold',
                fontSize: '13px',
                lineHeight: '20px',
                marginBottom: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {attachment.fileName || attachment.name || 'Untitled File'}
            </Typography>
            <Typography
              sx={{
                color: '#616161',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                lineHeight: '16px',
              }}>
              {attachment.createdAt
                ? `${getTimeAgo(attachment.createdAt)} by ${getUserName(
                    attachment.user,
                  )}`
                : 'Unknown date'}
            </Typography>
          </Box>

          {/* View and Download Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              gap: '8px',
            }}>
            <Box
              component="button"
              onClick={() => handleView(attachment)}
              sx={{
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                alignItems: 'center',
                backgroundColor: '#ffffff',
                border: '1px solid #dddddd',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                gap: '6px',
                padding: '8px',
              }}>
              <OpenInNewIcon
                sx={{
                  color: '#242424',
                  fontSize: '16px',
                }}
              />
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '14px',
                  lineHeight: '16px',
                }}>
                View
              </Typography>
            </Box>
            <IconButton
              onClick={() => handleDownload(attachment)}
              sx={{
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                backgroundColor: '#ffffff',
                border: '1px solid #dddddd',
                borderRadius: '6px',
                height: '32px',
                padding: '8px',
                width: '32px',
              }}>
              <DownloadIcon
                sx={{
                  color: '#242424',
                  fontSize: '16px',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: 'calc(100vh - 350px)',
        maxHeight: 'calc(100vh - 350px)',
        overflow: 'hidden',
        padding: '24px',
      }}>
      {/* Upload Button */}
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
        }}>
        <Box
          component="button"
          onClick={() => setUploadModalOpen(true)}
          sx={{
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            alignItems: 'center',
            backgroundColor: '#ffffff',
            border: '1px solid #dddddd',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            gap: '6px',
            height: '32px',
            padding: '4px 12px',
          }}>
          <ArrowUpwardIcon
            sx={{
              color: '#242424',
              fontSize: '12.5px',
            }}
          />
          <Typography
            sx={{
              color: '#242424',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}>
            Upload Files
          </Typography>
        </Box>
      </Box>

      {/* Attachments List */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '16px',
          minHeight: 0,
          overflow: 'auto',
        }}>
        {loading ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
            }}>
            <Typography
              sx={{
                color: '#616161',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '14px',
              }}>
              Loading attachments...
            </Typography>
          </Box>
        ) : attachments.length > 0 ? (
          attachments.map((attachment) => renderAttachmentCard(attachment))
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
            }}>
            <Typography
              sx={{
                color: '#6b7280',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '14px',
              }}>
              No attachments available
            </Typography>
          </Box>
        )}
      </Box>

      {/* File Upload Modal */}
      <FileUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        candidateId={candidateId}
        onUploadSuccess={getAttachments}
      />

      {/* File View Modal */}
      <Modal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        aria-labelledby="file-view-modal-title"
        aria-describedby="file-view-modal-description">
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            height: '90vh',
            left: '50%',
            maxWidth: '90%',
            outline: 'none',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
          }}>
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 24px',
            }}>
            <Typography
              id="file-view-modal-title"
              sx={{
                color: '#242424',
                fontFamily: 'Red Hat Display SemiBold',
                fontSize: '16px',
                fontWeight: 600,
              }}>
              {selectedFile?.fileName || selectedFile?.name || 'File View'}
            </Typography>
            <IconButton
              onClick={handleCloseViewModal}
              sx={{
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                color: '#6b7280',
              }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            id="file-view-modal-description"
            sx={{
              height: 'calc(90vh - 73px)',
              overflow: 'auto',
              padding: '24px',
            }}>
            {selectedFile && (
              <PdfViewer
                url={`${process.env.REACT_APP_APIURL}${
                  MASTER_ENDPOINT.Files
                }/${String(selectedFile.id)}?userId=${String(candidateId)}`}
                fileName={String(
                  selectedFile.fileName || selectedFile.name || '',
                )}
                candidateId={candidateId}
                hideDownload={false}
                lastUpdated={selectedFile.updatedAt || selectedFile.createdAt}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  candidateData,
  candidateId,
  jobData,
}) => {
  const renderOverview = () => {
    // Extract candidate summary, skills, strengths, and gaps from candidateData
    const summary =
      candidateData?.introduction ||
      candidateData?.summary ||
      candidateData?.profileSummary ||
      '';

    // Handle matched skills - could be array of strings or array of objects with skill.name
    let matchedSkills: string[] = [];
    if (candidateData?.matchedSkills) {
      matchedSkills = candidateData.matchedSkills.map(
        (skill: string | { name?: string }) =>
          typeof skill === 'string' ? skill : skill?.name || '',
      );
    } else if (candidateData?.skillsMatched) {
      matchedSkills = Array.isArray(candidateData.skillsMatched)
        ? candidateData.skillsMatched.map((skill: string | { name?: string }) =>
            typeof skill === 'string' ? skill : skill?.name || '',
          )
        : [];
    } else if (candidateData?.keyRequirements) {
      matchedSkills = Array.isArray(candidateData.keyRequirements)
        ? candidateData.keyRequirements.map((name?: any) =>
            typeof name === 'string' ? name : name?.requirement || '',
          )
        : [];
    } else if (candidateData?.candidateSkills) {
      // Extract from candidateSkills array
      matchedSkills = candidateData.candidateSkills
        .map((item: any) => item?.skill?.name || item?.skillName || '')
        .filter((name: string) => name);
    }

    const strengths = candidateData?.strengths || [];
    const gaps =
      candidateData?.gapsConcerns ||
      candidateData?.gaps ||
      candidateData?.concerns ||
      [];
    const recommendation = candidateData?.recommendation || '';

    // Helper component to render a card with header and content
    const renderCard = (
      title: string,
      content: React.ReactNode,
      includeEmoji = '',
    ) => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '8px',
        }}>
        {/* Card Header */}
        <Box
          sx={{
            backgroundColor: '#f7f7f7',
            border: '1px solid #e0e0e0',
            borderBottom: 'none',
            borderRadius: '12px 12px 0 0',
            padding: '8px 16px',
          }}>
          <Typography
            sx={{
              color: '#242424',
              fontFamily: 'Red Hat Display SemiBold',
              fontSize: '12px',
              fontWeight: 600,
            }}>
            {includeEmoji ? `${includeEmoji}  ${title}` : title}
          </Typography>
        </Box>
        {/* Card Content */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '0 0 12px 12px',
            borderTop: 'none',
            padding: '12px 16px',
          }}>
          {content}
        </Box>
      </Box>
    );

    return (
      <Box
        sx={{
          maxHeight: 'calc(100vh - 350px)',
          overflow: 'auto',
          padding: '24px',
        }}>
        {/* Summary Section */}
        {summary &&
          renderCard(
            'Summary',
            <Typography
              sx={{
                color: '#5b5b5b',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                lineHeight: '18px',
                whiteSpace: 'pre-wrap',
              }}>
              {summary}
            </Typography>,
          )}

        {/* Matched Skills Section */}
        {matchedSkills &&
          matchedSkills.length > 0 &&
          renderCard(
            'Matched Skills',
            <Box
              component="ul"
              sx={{
                color: '#5b5b5b',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                lineHeight: '18px',
                listStyle: 'disc',
                margin: 0,
                padding: 0,
              }}>
              {matchedSkills.map(
                (skill: string | { name?: string }, index: number) => {
                  const skillText =
                    typeof skill === 'string' ? skill : skill?.name || '';
                  return (
                    <li
                      key={index}
                      style={{
                        marginBottom:
                          index < matchedSkills.length - 1 ? '4px' : 0,
                        marginLeft: '18px',
                      }}>
                      <span style={{ lineHeight: '18px' }}>{skillText}</span>
                    </li>
                  );
                },
              )}
            </Box>,
          )}

        {/* Strengths and Gaps/Concerns Section */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            marginBottom: '8px',
          }}>
          {/* Strengths Card */}
          <Box sx={{ flex: 1 }}>
            {renderCard(
              'Strengths',
              strengths && strengths.length > 0 ? (
                <Box
                  component="ul"
                  sx={{
                    color: '#5b5b5b',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                    lineHeight: '18px',
                    listStyle: 'disc',
                    margin: 0,
                    padding: 0,
                  }}>
                  {strengths.map((strength: string, index: number) => (
                    <li
                      key={index}
                      style={{
                        marginBottom: index < strengths.length - 1 ? '4px' : 0,
                        marginLeft: '18px',
                      }}>
                      <span style={{ lineHeight: '18px' }}>{strength}</span>
                    </li>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: '#9ca3af',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  No strengths available
                </Typography>
              ),
              '✅',
            )}
          </Box>

          {/* Gaps/Concerns Card */}
          <Box sx={{ flex: 1 }}>
            {renderCard(
              'Gaps/Concerns',
              gaps && gaps.length > 0 ? (
                <Box
                  component="ul"
                  sx={{
                    color: '#5b5b5b',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                    lineHeight: '18px',
                    listStyle: 'disc',
                    margin: 0,
                    padding: 0,
                  }}>
                  {gaps.map((gap: string, index: number) => (
                    <li
                      key={index}
                      style={{
                        marginBottom: index < gaps.length - 1 ? '4px' : 0,
                        marginLeft: '18px',
                      }}>
                      <span style={{ lineHeight: '18px' }}>{gap}</span>
                    </li>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: '#9ca3af',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '12px',
                  }}>
                  No gaps identified
                </Typography>
              ),
              '⚠️️',
            )}
          </Box>
        </Box>

        {/* Recommendation Section */}
        {renderCard(
          'Recommendation',
          recommendation ? (
            <Typography
              sx={{
                color: '#5b5b5b',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
                lineHeight: '18px',
                whiteSpace: 'pre-wrap',
              }}>
              {recommendation}
            </Typography>
          ) : (
            <Typography
              sx={{
                color: '#9ca3af',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '12px',
              }}>
              No recommendation available
            </Typography>
          ),
          '📝',
        )}
      </Box>
    );
  };

  const renderNotes = () => {
    return <NotesComponent candidateId={candidateId} />;
  };

  const renderAttachments = () => {
    return (
      <AttachmentsComponent
        candidateId={candidateId}
        candidateData={candidateData}
        jobData={jobData}
      />
    );
  };

  switch (activeTab) {
    case 0:
      return renderOverview();
    case 1:
      return renderNotes();
    case 2:
      return renderAttachments();
    default:
      return renderOverview();
  }
};
